import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api'

export interface FeatureAccess {
  plan: string;
  features: string[];
  exclusiveFeatures: string[];
  limits: {
    profiles: number | 'unlimited';
    tokensPerMonth: number;
    storageGb: number;
  };
  teamsSupport: boolean;
  readonly: boolean;
  loading: boolean;
  error?: string;
}

export interface CanCreateCheck {
  canCreate: boolean;
  currentCount: number;
  limit: number | 'unlimited';
  plan: string;
}

export function useFeatureAccess() {
  const { currentUser, isAuthReady } = useAuth();
  const [access, setAccess] = useState<FeatureAccess>({
    plan: 'free',
    features: [],
    exclusiveFeatures: [],
    limits: {
      profiles: 1,
      tokensPerMonth: 10000,
      storageGb: 0.2,
    },
    teamsSupport: false,
    readonly: false,
    loading: true,
  });

  useEffect(() => {
    const fetchAccess = async () => {
      if (!currentUser || !isAuthReady) {
        setAccess(a => ({ ...a, loading: false }));
        return;
      }

      try {
        const token = await currentUser.getIdToken(true);
        const response = await fetch(`${API_BASE_URL}/api/v1/features/account-features`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAccess({
            plan: data.plan || 'free',
            features: data.features || [],
            exclusiveFeatures: data.exclusive_features || [],
            limits: {
              profiles: data.limits?.profiles ?? 1,
              tokensPerMonth: data.limits?.tokens_per_month ?? 10000,
              storageGb: data.limits?.storage_gb ?? 0.2,
            },
            teamsSupport: data.teams_support ?? false,
            readonly: data.readonly ?? false,
            loading: false,
          });
        } else {
          console.error('Failed to fetch feature access:', response.status);
          setAccess(a => ({ ...a, loading: false, error: 'Failed to load feature access' }));
        }
      } catch (error) {
        console.error('Error fetching feature access:', error);
        setAccess(a => ({ ...a, loading: false, error: error instanceof Error ? error.message : 'Unknown error' }));
      }
    };

    fetchAccess();
  }, [currentUser, isAuthReady]);

  const canCreateProfile = async (): Promise<CanCreateCheck> => {
    if (!currentUser) {
      return {
        canCreate: false,
        currentCount: 0,
        limit: 1,
        plan: 'free',
      };
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/features/can-create-profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          canCreate: data.can_create ?? false,
          currentCount: data.current_count ?? 0,
          limit: data.limit ?? 1,
          plan: data.plan ?? 'free',
        };
      }
    } catch (error) {
      console.error('Error checking profile creation:', error);
    }

    return {
      canCreate: false,
      currentCount: 0,
      limit: 1,
      plan: 'free',
    };
  };

  const canCreateWellnessGoal = async (): Promise<CanCreateCheck> => {
    if (!currentUser) {
      return {
        canCreate: false,
        currentCount: 0,
        limit: 5,
        plan: 'free',
      };
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/features/can-create-wellness-goal`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          canCreate: data.can_create ?? false,
          currentCount: data.current_count ?? 0,
          limit: data.limit ?? 5,
          plan: data.plan ?? 'free',
        };
      }
    } catch (error) {
      console.error('Error checking wellness goal creation:', error);
    }

    return {
      canCreate: false,
      currentCount: 0,
      limit: 5,
      plan: 'free',
    };
  };

  const canUploadGeneticData = async (profileId?: string): Promise<CanCreateCheck> => {
    if (!currentUser) {
      return {
        canCreate: false,
        currentCount: 0,
        limit: 1,
        plan: 'free',
      };
    }

    try {
      const token = await currentUser.getIdToken();
      const url = new URL(`${API_BASE_URL}/api/v1/features/can-upload-genetic-data`);
      if (profileId) {
        url.searchParams.append('profile_id', profileId);
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          canCreate: data.can_upload ?? false,
          currentCount: data.current_count ?? 0,
          limit: data.limit ?? 1,
          plan: data.plan ?? 'free',
        };
      }
    } catch (error) {
      console.error('Error checking genetic upload:', error);
    }

    return {
      canCreate: false,
      currentCount: 0,
      limit: 1,
      plan: 'free',
    };
  };

  const canAccessSection = async (sectionId: string): Promise<{ canAccess: boolean; requiredTier: string; upgradeRequired: boolean }> => {
    if (!currentUser) {
      return {
        canAccess: false,
        requiredTier: 'basic',
        upgradeRequired: true,
      };
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/features/can-access-section/${sectionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          canAccess: data.can_access ?? false,
          requiredTier: data.required_tier ?? 'basic',
          upgradeRequired: data.upgrade_required ?? true,
        };
      }
    } catch (error) {
      console.error('Error checking section access:', error);
    }

    return {
      canAccess: false,
      requiredTier: 'basic',
      upgradeRequired: true,
    };
  };

  const canAccessCustomReports = async (): Promise<{ canAccess: boolean; requiredTier: string; upgradeRequired: boolean }> => {
    if (!currentUser) {
      return {
        canAccess: false,
        requiredTier: 'basic',
        upgradeRequired: true,
      };
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/features/can-access-custom-reports`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          canAccess: data.can_access ?? false,
          requiredTier: data.required_tier ?? 'basic',
          upgradeRequired: data.upgrade_required ?? true,
        };
      }
    } catch (error) {
      console.error('Error checking custom reports access:', error);
    }

    return {
      canAccess: false,
      requiredTier: 'basic',
      upgradeRequired: true,
    };
  };

  const canCreateCustomReport = async (): Promise<{
    canCreate: boolean;
    currentCount: number;
    limit: number | 'unlimited';
    upgradeRequired: boolean;
    limitResetAt?: string | null;
  }> => {
    if (!currentUser) {
      return { canCreate: false, currentCount: 0, limit: 0, upgradeRequired: true, limitResetAt: null };
    }
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/features/can-create-custom-report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        return {
          canCreate: data.can_create ?? false,
          currentCount: data.current_count ?? 0,
          limit: data.limit === 'unlimited' ? 'unlimited' : (data.limit ?? 0),
          upgradeRequired: data.upgrade_required ?? false,
          limitResetAt: data.limit_reset_at ?? null,
        };
      }
    } catch (error) {
      console.error('Error checking custom report creation:', error);
    }
    return { canCreate: false, currentCount: 0, limit: 0, upgradeRequired: true, limitResetAt: null };
  };

  return {
    ...access,
    canCreateProfile,
    canCreateWellnessGoal,
    canUploadGeneticData,
    canAccessSection,
    canAccessCustomReports,
    canCreateCustomReport,
  };
}
